import React from 'react';
import PropTypes from 'prop-types';
import Modal from 'apollo-react/components/Modal';
import Button from 'apollo-react/components/Button';
import Box from 'apollo-react/components/Box';
import styles from './CommonModal.module.css';

/**
 * CommonModal - Shared modal wrapper that guarantees footer buttons are shown
 * when buttonProps is provided, including when custom children are rendered.
 * Use this instead of apollo-react Modal directly across the project.
 *
 * @param {boolean} open - Whether the modal is open
 * @param {Function} onClose - Callback when modal is closed
 * @param {string} title - Modal title
 * @param {string} message - Simple message content (use when no children)
 * @param {string} size - 'small' | 'medium' | 'large'
 * @param {Array} buttonProps - Array of { label, onClick, variant?, status?, disabled? }
 * @param {boolean} hideHeader - Hide the header
 * @param {string} variant - Modal variant (e.g. 'warning')
 * @param {string} id - Modal id for accessibility
 * @param {React.ReactNode} children - Custom modal content (buttons still rendered in footer when buttonProps provided)
 */
const CommonModal = ({
  open = false,
  onClose,
  title,
  message,
  size = 'medium',
  buttonProps,
  hideHeader,
  hideButtons,
  variant,
  id,
  children,
  className,
  ...rest
}) => {
  const hasCustomContent = Boolean(children);
  const hasButtons = Array.isArray(buttonProps) && buttonProps.length > 0;
  const renderOwnFooter = hasButtons;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      message={!renderOwnFooter && !hasCustomContent ? message : undefined}
      size={size}
      buttonProps={
        !renderOwnFooter && !hasCustomContent ? buttonProps : undefined
      }
      hideHeader={hideHeader}
      hideButtons={renderOwnFooter ? true : hideButtons}
      variant={variant}
      id={id}
      className={className}
      {...rest}
    >
      {renderOwnFooter && (
        <>
          <Box className={styles.content}>
            {hasCustomContent ? children : message}
          </Box>
          <Box className={styles.footer}>
            {buttonProps.map((btn, index) => {
              const isFirst = index === 0;
              const isLast = index === buttonProps.length - 1;
              const variant =
                btn.variant || btn.color || (isLast ? 'primary' : 'secondary');
              const status = btn.status || (isFirst ? 'danger' : undefined);
              const label =
                btn.label || (isFirst ? 'Cancel' : isLast ? 'Confirm' : '');
              return (
                <Button
                  key={index}
                  variant={variant}
                  status={status}
                  onClick={btn.onClick}
                  disabled={btn.disabled}
                  // size="small"
                >
                  {label}
                </Button>
              );
            })}
          </Box>
        </>
      )}
      {!renderOwnFooter && hasCustomContent && children}
    </Modal>
  );
};

CommonModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  title: PropTypes.string,
  message: PropTypes.string,
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  buttonProps: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      onClick: PropTypes.func.isRequired,
      variant: PropTypes.string,
      status: PropTypes.string,
      disabled: PropTypes.bool,
    })
  ),
  hideHeader: PropTypes.bool,
  hideButtons: PropTypes.bool,
  variant: PropTypes.string,
  id: PropTypes.string,
  children: PropTypes.node,
  className: PropTypes.string,
};

export default CommonModal;
